import React, { useEffect, useState } from "react";
import { useFieldArray } from "react-hook-form";
import ControlledDatePicker from "../../../components/Custom/Forms/Controller/ControlledDatePicker";
import ControlledInput from "../../../components/Custom/Forms/Controller/ControlledInput";
import { MdDelete, MdEdit, MdOutlineCheck, MdVerified } from "react-icons/md";
import { Button } from "reactstrap";
import { useDispatch } from "react-redux";
import { Delete_Settelment } from "../../../redux/slices/merchantSlice";
import { useParams } from "react-router-dom";
import Loader from "../../../components/Custom/Loader";
import { responseToaster } from "../../../helperFunctions";
import DeleteModal from "../../../components/Custom/Modals/DeleteModal/DeleteModal";

export default function TimeInputManager({
  control,
  getValues,
  watch,
  setError,
  clearErrors,
  errors,
  disabled = false,
}) {
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "settlements",
  });
  const dispatch = useDispatch();
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [pendingMaxTimeIndex, setPendingMaxTimeIndex] = useState(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { id } = useParams();

  const handleVerify = (index) => {
    const item = getValues(`settlements.${index}`);
    let hasError = false;

    const selectedTime = new Date(item?.time);
    const selectedMinutes = selectedTime?.getHours?.() * 60 + selectedTime?.getMinutes?.();

    if (!item?.time) {
      setError(`settlements.${index}.time`, {
        type: "manual",
        message: "DailyAt is required",
      });
      hasError = true;
    } else {
      clearErrors(`settlements.${index}.time`);
    }

    if (!item?.description) {
      setError(`settlements.${index}.description`, {
        type: "manual",
        message: "Percentage is required",
      });
      hasError = true;
    } else if (
      isNaN(item?.description) ||
      Number(item?.description) < 0 ||
      Number(item?.description) > 100
    ) {
      setError(`settlements.${index}.description`, {
        type: "manual",
        message: "Percentage must be between 0 and 100",
      });
      hasError = true;
    } else {
      clearErrors(`settlements.${index}.description`);
    }

    if (hasError) return;

    const isMaxTime = selectedTime?.getHours?.() === 23 && selectedTime?.getMinutes?.() === 59;

    const allSettlements = getValues("settlements");
    const hasInvalidFutureTime = allSettlements
      ?.slice?.(index + 1)
      ?.some?.((row) => {
        if (!row?.verified || !row?.time) return false;
        const rowTime = new Date(row?.time);
        const rowMinutes = rowTime?.getHours?.() * 60 + rowTime?.getMinutes?.();
        return selectedMinutes >= rowMinutes;
      });

    if ((isMaxTime || hasInvalidFutureTime) && index < fields?.length - 1) {
      setPendingMaxTimeIndex(index);
      return;
    }

    update(index, { ...item, verified: true });
  };


  const handleConfirmMaxTime = async () => {
    setIsConfirmingDelete(true);
    const index = pendingMaxTimeIndex;
    const item = getValues(`settlements.${index}`);

    try {
      for (let i = fields?.length - 1; i > index; i--) {
        await handleDelete(i)
      }
      update(index, { ...item, verified: true });
      setPendingMaxTimeIndex(null);
    } catch (error) {
      console.error("Error while deleting future settlements:", error);
    } finally {
      setIsConfirmingDelete(false);
    }

  };


  const handleDelete = async (index) => {
    try {
      const item = getValues(`settlements.${index}`);
      if (!item?.id) {
        remove(index);
        return;
      }
      setDeletingIndex(index);
      const payload = {
        id: item?.id,
        merchantId: id
      }
      const res = await dispatch(Delete_Settelment(payload)).unwrap();
      responseToaster(res);
      remove(index);
    } catch (error) {
      console.log('error', error)
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleEdit = (index) => {
    const item = getValues(`settlements.${index}`);
    update(index, { ...item, verified: false });
  };

  const handleAdd = () => {
    append({ time: "", description: "", verified: false });
  };

  const getMaxVerifiedTime = () => {
    const values = getValues("settlements");
    const verifiedTimes = values
      ?.filter?.(item => item?.verified && item?.time)
      ?.map?.(item => new Date(item?.time));

    if (!verifiedTimes?.length) return null;

    const max = new Date(Math?.max?.(...verifiedTimes));
    max.setFullYear(0, 0, 0);
    return max;
  };

  const maxVerifiedTime = getMaxVerifiedTime();

  const isMaxTimeReached = () => {
    if (!maxVerifiedTime) return false;
    return (
      maxVerifiedTime?.getHours?.() === 23 && maxVerifiedTime?.getMinutes?.() === 59
    );
  };

  return (
    <>
      <div className="w-100">
        {fields?.length === 0 && (
          <div className="d-flex justify-content-start mt-1">
            <Button
              title="Add"
              color="primary"
              className="btn-smallsize"
              onClick={handleAdd}
              disabled={disabled}
            >
              Add Settlement
            </Button>
          </div>
        )}

        {fields?.map?.((field, index) => {
          const prefix = `settlements.${index}`;
          const verified = watch(`${prefix}.verified`);

          const previousVerifiedTimes = fields
            .slice?.(0, index)
            .map?.((_, i) => getValues(`settlements.${i}`))
            .filter?.((item) => item?.verified && item?.time)
            .map?.((item) => new Date(item?.time));

          const maxPrevVerified = previousVerifiedTimes.length
            ? new Date(Math.max?.(...previousVerifiedTimes?.map?.((t) => t?.getTime?.())))
            : null;

          const minTime = maxPrevVerified
            ? new Date(maxPrevVerified?.getTime?.() + 60000)
            : new Date(0, 0, 0, 0, 0);

          return (
            <div className="row align-items-start" key={field?.id}>
              {verified ? (
                <>
                  <ControlledDatePicker
                    name={`${prefix}.time`}
                    className="col-3 "
                    showTimeSelect
                    showTimeSelectOnly
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    timeCaption="Time"
                    timeIntervals={1}
                    readOnly
                    placeholder="Select DailyAt"
                    label={`DailyAt`}
                    isClearable={false}
                    control={control}
                    err={errors?.settlements?.[index]?.time?.message}
                    disabled={true}
                  />

                  <ControlledInput
                    name={`${prefix}.description`}
                    className="col-3"
                    label="Percentage"
                    placeholder="Enter Percentage"
                    control={control}
                    err={errors?.settlements?.[index]?.description?.message}
                    disabled={true}
                  />

                  <div className="col-2 d-flex align-items-center gap-1" style={{ marginTop: "38px" }}>
                    <Button
                      title="Edit"
                      color="primary"
                      className="btn-smallsize"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEdit(index)
                      }}
                      disabled={disabled}
                    >
                      <MdEdit />
                    </Button>
                    <Button
                      title="Delete"
                      color="danger"
                      className="btn-smallsize"
                      onClick={() => handleDelete(index)}
                      disabled={disabled || deletingIndex === index}
                    >
                      {deletingIndex === index ? <Loader /> : <MdDelete />}
                    </Button>

                    <MdVerified color="green" />
                    {index === fields?.length - 1 && <Button
                      title="Add"
                      color="primary"
                      className="btn-smallsize"
                      onClick={handleAdd}
                      disabled={disabled || isMaxTimeReached()}
                    >
                      Add More
                    </Button>}
                  </div>
                </>
              ) : (
                <>
                  <ControlledDatePicker
                    name={`${prefix}.time`}
                    className="col-3 cross_icon"
                    showTimeSelect
                    showTimeSelectOnly
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    timeCaption="Time"
                    timeIntervals={1}
                    readOnly
                    placeholder="Select DailyAt"
                    label={`DailyAt`}
                    isClearable
                    minTime={minTime}
                    maxTime={new Date(0, 0, 0, 23, 59)}
                    control={control}
                    err={errors?.settlements?.[index]?.time?.message}
                    disabled={disabled}
                  />

                  <ControlledInput
                    name={`${prefix}.description`}
                    className="col-3"
                    label="Percentage"
                    type="number"
                    placeholder="Enter Percentage"
                    disabled={disabled}
                    control={control}
                    err={errors?.settlements?.[index]?.description?.message}
                  />

                  <div className="col-3 d-flex gap-1" style={{ marginTop: "38px" }}>
                    <Button
                      title="Verify"
                      color="primary"
                      className="btn-smallsize"
                      onClick={() => handleVerify(index)}
                      disabled={disabled}
                    >
                      <MdOutlineCheck />
                    </Button>
                    <Button
                      title="Delete"
                      color="danger"
                      className="btn-smallsize"
                      onClick={() => handleDelete(index)}
                      disabled={disabled || deletingIndex === index}
                    >
                      {deletingIndex === index ? <Loader /> : <MdDelete />}
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <DeleteModal
        title="Confirm Maximum Time"
        content="Changing this time will remove all settlements after this entry. Do you want to continue?"
        isOpen={pendingMaxTimeIndex !== null}
        onDoneClick={handleConfirmMaxTime}
        onCancelClick={() => setPendingMaxTimeIndex(null)}
        isButtonLoading={isConfirmingDelete}
      />
    </>
  );
}
